// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useRef} from 'react';
import styled from 'styled-components';
import QRCode from '../_common/api/QRCode';
import { QuestRoute } from '../_common/api/QuestRoutes';
import { UseDeviceId } from '../_common/api/usePersistedState';
import CountDown from '../_common/CountDown';
import Button from '../_common/form/Button';
import Input from '../_common/form/Input';
import { useGlobalState } from '../_common/globalState/GlobalStateProvider';
import LocationList from '../_common/LocationList';
import Modal from '../_common/Modal';
import Card from './Card';
import { HamQuest } from '../../App';
import QRModal from '../_common/QRModal';
import { useState } from 'react';
import { Features, HasFeature } from '../_common/RequireFeature';

const Col = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`
const Row = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
`

const ManageChildrenContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 50vh;
    overflow: hidden;
    padding: 0;
`

const ChildrenScrollArea = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    padding-right: 0.5em;
    margin-right: -0.5em;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 3px;

        &:hover {
            background: rgba(0, 0, 0, 0.3);
        }
    }
`

const ChildAccountCard = styled(Card)`
    & > div > div > div:first-child {
        font-size: 0.9em;
    }
`

const AddChildButtonContainer = styled.div`
    flex-shrink: 0;
    padding-top: 1em;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin-top: 0.75em;
    display: flex;
    justify-content: center;
`
 // seconds

export const generateQR = (code, token, offline, expireTime) => {
    // const body = (
    //     <Card noSlide>
    //         <QRCode data={code} />
    //         {token ? <CountDown type='bar' time={30} callback={() => Modal.Close()} /> : null}
    //         <Row>{!token ? code : null}</Row>
    //     </Card>
    // )
    Modal.Close();

    const body = (
        <Col>
            <QRCode data={code} />
            {token ? <CountDown type='bar' time={expireTime} callback={() => QRModal.Close()} /> : null}
            <Row>{!token ? code : null}</Row>
        </Col>
    )
    QRModal.Show({
        body: body,
        type: 'success'
    })
}

const WashClub = ({ generate, onAddChild, ...props }) => {
    const { state: { active_user, }, getWashClub } = useGlobalState();
    const { washClub } = active_user;
    const paygEnabled = HasFeature(Features.PAYG);
    const WCActive = washClub.status.toLowerCase() === 'active' || washClub.status.toLowerCase() === 'endbillingpending';
    const generateCodeTimeout = useRef(null);
    const [showUseCount, setShowUseCount] = useState(false);

    const hasFamilyPlanEnabled = HasFeature({ all: [Features.FamilyPlans, Features.PWAFamilyPlanManagement] });
    const [expireTime, setExpireTime] = useState(30);
    useEffect(() => {
        getWashClub();
        HamQuest.get(QuestRoute.merchant.support)
            .then(res => {
                setShowUseCount(res['features.WashClub.DisplayUseCount']);
        });

        HamQuest.post(QuestRoute.tempToken, { TempIdType: 'WashClub' }, {
            headers: {
                deviceId: 'test-device-id'
            }
        }).then(res => { setExpireTime(res.expirationTime);
        }).catch(ex => { console.log('call to set time failed', ex); });

        if (!generate) return;
        generateWCCode();
    }, [])

    if (washClub.payg && !paygEnabled) return null;

    const generateWCCode = async () => {
        if(generateCodeTimeout.current) return;

        HamQuest.post(QuestRoute.tempToken, { TempIdType: 'WashClub' }, {
            headers: {
                deviceId: await UseDeviceId()
            }
        })
        .then((res) => {
            if (!res.tempId) throw res.error;
            generateQR(res.tempId, true, false, expireTime)
        })
        .catch(ex => {
            Modal.Show({
                type: 'error',
                title: 'Unable to Generate Token',
                body: ex.blob || 'Sorry, something went wrong. Please try again.'
            })
        })
        .finally(() =>{
            generateCodeTimeout.current = setTimeout(() => {
                generateCodeTimeout.current = null;
            }, 2000);
        });
    }

    
    const manageChild = () => {
        Modal.Show({
            title: 'Manage Child Accounts',
            body: (
                <ManageChildrenContainer>
                    <ChildrenScrollArea>
                        {washClub.children.filter(child => !child.email.includes('deleteduser.com')).map(child => (
                            <ChildAccountCard title={child.email} icon='user'>
                                <Button onClick={() => {
                                    HamQuest.post(QuestRoute.user.cancelChild, {
                                        ChildUserId: child.userId
                                    })
                                        .then((res) => {
                                            Modal.Show({
                                                type: 'success',
                                                title: 'Child Account Removed',
                                                body: `${child.email} has had their Wash Club cancelled.`
                                            });
                                        }
                                        ).catch(ex => {
                                            Modal.Show({
                                                type: 'error',
                                                title: 'Unable to Remove Child Account',
                                                body: ex.blob || 'Sorry, something went wrong. Please try again.'
                                            })
                                        })
                                }} 
                                icon='delete'
                                disabled={child.clubStatus.toLowerCase() !== 'active'}
                                >{ child.clubStatus.toLowerCase() === 'active' ? 'Remove Child Account'  : 'Inactive'}</Button>
                            </ChildAccountCard>
                        ))}
                    </ChildrenScrollArea>
                    <AddChildButtonContainer>
                        <Button onClick={onAddChild} icon='user'>Add A Child Account</Button>
                    </AddChildButtonContainer>
                </ManageChildrenContainer>
            ),
            type: 'success',
            closeAsX: true,
        })
    }

    return (
        <Card title='Wash Club' icon='calendar'>
            <Input
                standalone
                label='Wash Club'
                value={washClub.name}
                disabled
            />
            { !washClub.payg && showUseCount && 
            <>
            <Input
                standalone
                label='Current Uses'
                value={active_user.washClub.useCountDisplay}
                disabled
            />
            </>}
            { !washClub.payg && <>
            <Input
                standalone
                label='License Plate'
                value={washClub.appUserVehicle?.license}
                disabled
            />
            <Input
                standalone
                label='Next Billing Date'
                value={new Date(washClub.nextBillingDate).toLocaleDateString()}
                disabled
            />
            </>}

            <LocationList itemLocations={washClub?.locations} />

            { hasFamilyPlanEnabled ?
            !washClub.parent && (!washClub.children || washClub.children.length === 0) && <Button onClick={onAddChild} icon='user'>Add Child</Button>
            : null }
            
            {hasFamilyPlanEnabled ?
            WCActive && !washClub.parent && washClub.children && washClub.children.length > 0 && <Button onClick={manageChild} icon='user'>Manage Child Accounts</Button>
            : null }

            {WCActive &&
                <>
                    <Button onClick={generateWCCode} icon='QRcode'>Generate Club Token</Button>
                    <span style={{ fontSize: '0.6em', fontWeight: 600, textAlign: 'center' }}>
                        Club Codes Expire {expireTime} Seconds after Generation.
                    </span>
                </>
            }
            {hasFamilyPlanEnabled && !washClub.parent && onAddChild && (!washClub.children || washClub.children.length === 0) && <Button onClick={onAddChild} icon='user'>Add Child</Button>}
            {WCActive && hasFamilyPlanEnabled && !washClub.parent && washClub.children && washClub.children.length > 0 && <Button onClick={manageChild} icon='user'>Manage Child Accounts</Button>}

        </Card>
    )
}

export default WashClub;